import { capitalize } from "lodash";
import AppContext from "PFApp/app_context";
import { ProfileItem } from "PFApp/components/profile_item";
import { useGrowl } from "PFApp/use_growl";
import { ActionIcon } from "PFComponents/action_icon";
import { Button } from "PFComponents/button";
import Card from "PFComponents/card/card";
import { Icon } from "PFComponents/icon";
import Modal from "PFComponents/modal/modal";
import AutoSelect from "PFComponents/select/autoselect";
import { ProfilePill } from "PFComponents/select/profile_pill";
import { sendInvitation } from "PFCore/services/invites/send_invitation";
import { fetchProfilesOptions } from "PFCore/services/profile";
import SearchIcon from "PFIcons/search_fat.svg";
import PropTypes from "prop-types";
import React, { useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import { useActivityHistoryInvalidate } from "../../activities/show/history/hooks/use_activity_history_invalidate";
import css from "./share_button.module.scss";

const ShareButton = ({ activityIds, invites, classes, onSuccess, activityType, kind, withTitle = true }) => {
  const {
    store: { currentProfile }
  } = useContext(AppContext);
  const growl = useGrowl();
  const { t } = useTranslation("core");
  const invalidateHistory = useActivityHistoryInvalidate(activityIds);

  const [shown, setShown] = useState(false);
  const [profiles, setProfiles] = useState([]);

  const noActivityIds = activityIds.length === 0;
  const handleClose = () => {
    setShown(false);
    setProfiles([]);
  };
  const handleShare = () => {
    handleClose();
    sendInvitation({
      targetIds: activityIds,
      targetType: "Task",
      profileIds: profiles.map(({ id }) => id)
    })
      .then((resp) => {
        growl({
          message: activityType
            ? t("activityShare.inviteMessage.activitiesShared", { activityType: capitalize(activityType) })
            : t("activityShare.inviteMessage.success")
        });
        onSuccess?.(resp);
        invalidateHistory();
      })
      .catch(() =>
        growl({
          kind: "error",
          message: t("activityShare.inviteMessage.error")
        })
      );
  };
  return (
    <>
      <Button theme={classes} disabled={noActivityIds} onClick={() => setShown(true)} kind={kind}>
        {withTitle && <span>{t("activityShare.buttonText")}</span>} <Icon name="share" size="sm" />
      </Button>
      {shown && (
        <Modal handleClose={handleClose} style={{ maxWidth: 500, margin: "0 auto" }}>
          <Card>
            <h3 className={css.title}>
              {activityType
                ? t("activityShare.shareActivityTemplates", {
                    count: activityIds.length,
                    templateKey: activityType
                  })
                : t("activityShare.shareActivities", { count: activityIds.length })}
            </h3>
            <ActionIcon name="cross" size="xs" onClick={handleClose} classes={{ root: css.closeButton }} />
            <AutoSelect
              key={profiles.length}
              kind="bordered"
              style={{ marginTop: 50 }}
              query={(term) => fetchProfilesOptions({ term })}
              formatOption={(item) => ({
                id: item.id,
                displayElement: <ProfileItem profile={item} />,
                item: item
              })}
              filterOptions={(options) =>
                options.filter(
                  (option) =>
                    currentProfile.id !== option.id &&
                    !profiles.find(({ id }) => id === option.id) &&
                    !(invites || []).find(({ profile_id }) => profile_id === option.id)
                )
              }
              label={t("activityShare.buttonLabel")}
              placeholder={t("activityShare.buttonPlaceholder")}
              value={profiles}
              values={profiles}
              renderItem={({ key, value, onRemove }) => (
                <ProfilePill key={key} profile={value.item} handleRemove={onRemove} />
              )}
              icon={<SearchIcon height={20} width={20} style={{ marginRight: 5 }} />}
              controlledValue
              closeOnChange
              handleChange={setProfiles}
              noPadding
              multi
              cache={false}
            />
            <div className={css.info}>
              <span className={css.label}>{t("translation:pleaseNote")}</span>
              <span>{t("activityShare.buttonInfo")}</span>
            </div>
            <div className={css.buttons}>
              <Button text={t("translation:cancel")} kind="tertiary" onClick={handleClose} />
              <Button
                text={t("activityShare.buttonText")}
                onClick={handleShare}
                disabled={profiles.length === 0}
              />
            </div>
          </Card>
        </Modal>
      )}
    </>
  );
};

ShareButton.propTypes = {
  activityType: PropTypes.string,
  activityIds: PropTypes.arrayOf(PropTypes.number).isRequired,
  invites: PropTypes.array,
  classes: PropTypes.object,
  onSuccess: PropTypes.func,
  kind: PropTypes.string,
  withTitle: PropTypes.bool
};

export default ShareButton;
